import { Component, OnInit, ViewChild, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Unsubscribable, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  IAlertType,
  ISiteAlertSeverity,
  EntityCacheProvider,
  ValuesExchangeProvider,
  TransformationService,
  UserService,
  UserAuth,
  IAlertListRow,
  PaginatorComponent
} from '@nrwl/common-lib';

import { ProfileSettingsUpdater } from '../../../profile-settings-updater';
import { WizardNavigatorService } from '../../../wizard-navigator.service';

@Component({
  selector: 'nrwl-site-alerts',
  templateUrl: './site-alerts.component.html',
  styleUrls: ['./site-alerts.component.scss']
})
export class SiteAlertsComponent
  extends ProfileSettingsUpdater
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginator', { static: true }) paginatorComponent: PaginatorComponent;
  // eslint-disable-next-line
  @Input() dataSource: BehaviorSubject<any>;
  sitesdisplayedColumns: string[] = ['sites', 'minimumseverity', 'remove'];
  // eslint-disable-next-line
  selectedSiteList: MatTableDataSource<any>;
  showSiteAddList = true;
  siteSelectAll = false;
  defaultSiteChecked = false;
  // eslint-disable-next-line
  siteMinimumSeverity: any;
  minimumSeverity: string[];
  // eslint-disable-next-line
  siteList: any[] = [];
  // eslint-disable-next-line
  alertPreferenceData: any;
  selectedSitesUnsubscribable: Unsubscribable;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly entityCache: EntityCacheProvider,
    private readonly exchange: ValuesExchangeProvider,
    private readonly transformService: TransformationService,
    private readonly wizardNavigatorService: WizardNavigatorService,
    userService: UserService,
    activatedRoute: ActivatedRoute,
    userAuth: UserAuth
  ) {
    super(userService, activatedRoute, userAuth);
  }

  ngOnInit() {
    // eslint-disable-next-line
    this.selectedSiteList = new MatTableDataSource<any>([]);
    this.minimumSeverity = ['warning', 'critical'];
    this.siteMinimumSeverity = 'warning';
    this.loadNotificationPreference();
    this.setCustomSites();
    this.selectedSitesUnsubscribable = this.exchange.registerInterest(
      'selectedSites',
      (selectedSites: IAlertListRow[]) => {
        if (selectedSites) {
          selectedSites.forEach(site => this.siteList.push(site));
          this.selectedSiteList.data = this.siteList;
          this._setSiteAlerts();
          this._saveAlerts();
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.selectedSiteList.paginator = this.paginatorComponent.paginator;
  }

  nextPage(event: PageEvent): void {
    this.selectedSiteList.paginator.pageIndex = event.pageIndex;
  }

  ngOnDestroy(): void {
    this.exchange.notifyListeners('selectedSites', null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribeUpdates();
    if (this.selectedSitesUnsubscribable) {
      this.selectedSitesUnsubscribable.unsubscribe();
    }
  }

  // eslint-disable-next-line
  openSiteSelectionWizard(wizardId): void {
    this.exchange.notifyListeners('sites-filter', this.siteList);
    this.wizardNavigatorService.navigateToWizard(wizardId);
  }

  setCustomSites(): void {
    this.dataSource.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      const siteList = this.entityCache.findEntityCache('site').getValues();
      const filterSiteList = this.transformService.transformData('user-site-management', siteList);
      if (data.performanceAlertConfiguration) {
        const alerts: ISiteAlertSeverity[] =
          data.performanceAlertConfiguration.performanceAlertsBySites.customSites;
        alerts?.forEach(alert => {
          const site = filterSiteList.data.find(s => s.id === alert.siteId);
          this.siteList.push({
            siteId: alert.siteId,
            minimumSeverity: alert.minimumSeverity,
            name: site.name
          });
        });
      }
      this.selectedSiteList.data = this.siteList;
    });
  }

  // eslint-disable-next-line
  changeApplication(event): void {
    switch (event.value) {
      case 'sites': {
        this.showSiteAddList = false;
        this.siteSelectAll = true;
        this.siteList = [];
        this.selectedSiteList.data = [];
        break;
      }
      case 'custom-sites': {
        this.siteSelectAll = false;
        this.showSiteAddList = true;
        break;
      }
    }

    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsBySites.allSites.selectAll = this.siteSelectAll;
    this._setSiteAlerts();
    this._saveAlerts();
  }

  // eslint-disable-next-line
  minimumSeverityChange(event, value, row?: IAlertListRow): void {
    if (value === 'allSite') {
      this.siteMinimumSeverity = event.value;
    } else if (value === 'customSiteSeverity' && row) {
      const site = this.siteList.find(a => a.siteId === row.siteId);
      if (site) {
        site.minimumSeverity = event.value;
      }
    }

    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsBySites.allSites.minimumSeverity = this.siteMinimumSeverity;
    this._setSiteAlerts();
    this._saveAlerts();
  }

  removeSite(siteRow: IAlertListRow): void {
    const index = this.siteList.findIndex(site => site.siteId === siteRow.siteId);
    this.siteList.splice(index, 1);
    this._setSiteAlerts();
    this._saveAlerts();
    this.updateTableDataSource();
  }

  updateTableDataSource(): void {
    this.selectedSiteList.data = this.siteList;
  }

  private _saveAlerts(): void {
    this.updateUserAlertsPreferences(this.alertPreferenceData);
  }

  private _setSiteAlerts(): void {
    const alerts: ISiteAlertSeverity[] = this.siteList.map(site => {
      return {
        siteId: site.siteId,
        minimumSeverity: site.minimumSeverity,
        alertType: IAlertType.APP
      } as ISiteAlertSeverity;
    });
    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertsBySites.customSites = alerts;
  }

  private loadNotificationPreference(): void {
    this.dataSource.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.alertPreferenceData = data;
      const performanceAlertConfiguration = data.performanceAlertConfiguration;
      if (performanceAlertConfiguration) {
        this.siteSelectAll =
          performanceAlertConfiguration.performanceAlertsBySites.allSites.selectAll;
        this.siteMinimumSeverity =
          performanceAlertConfiguration.performanceAlertsBySites.allSites.minimumSeverity;
        this.defaultSiteChecked = this.siteSelectAll;
        this.showSiteAddList = !this.siteSelectAll;
      }
    });
  }
}
